import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BillingSettings = ({ token }) => {
    const [config, setConfig] = useState({
        business_name: '',
        business_address: '',
        business_phone: '',
        gstin: '',
        gst_percent: 0,
        service_charge: 0,
        currency_symbol: '₹',
        footer_note: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=get_config', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const json = await res.json();
                if (json.success && json.config) {
                    setConfig(json.config);
                }
            } catch (err) {
                console.error("Fetch billing config error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, [token]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMsg({ type: '', text: '' });
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=save_config', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(config)
            });
            const json = await res.json();
            if (json.success) {
                setMsg({ type: 'success', text: 'Billing settings saved successfully!' });
            } else {
                setMsg({ type: 'error', text: json.message });
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Network error saving settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading billing settings...</div>;

    return (
        <div style={{ maxWidth: '600px' }}>
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>

            <h2>Billing & Tax Configuration</h2>
            <p style={{ color: '#64748b', marginBottom: '25px', fontSize: '0.9em' }}>
                Setup your business details for professional invoices and automated tax calculations.
            </p>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Business/Restaurant Name (on Bill)</label>
                    <input 
                        type="text" 
                        value={config.business_name} 
                        onChange={e => setConfig({...config, business_name: e.target.value})}
                        placeholder="e.g. My Cafe & Restaurant"
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        required
                    />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Business Address</label>
                    <textarea 
                        value={config.business_address} 
                        onChange={e => setConfig({...config, business_address: e.target.value})}
                        placeholder="Complete address for invoice header"
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '80px' }}
                        required
                    />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Business Phone Number</label>
                    <input 
                        type="text" 
                        value={config.business_phone} 
                        onChange={e => setConfig({...config, business_phone: e.target.value})}
                        placeholder="e.g. +91 9876543210"
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>GSTIN / Tax ID</label>
                        <input 
                            type="text" 
                            value={config.gstin} 
                            onChange={e => setConfig({...config, gstin: e.target.value})}
                            placeholder="Optional"
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Currency</label>
                        <input 
                            type="text" 
                            value={config.currency_symbol} 
                            onChange={e => setConfig({...config, currency_symbol: e.target.value})}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>GST Percent (%)</label>
                        <input 
                            type="number" 
                            step="0.01"
                            value={config.gst_percent} 
                            onChange={e => setConfig({...config, gst_percent: parseFloat(e.target.value) || 0})}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Service Charge (%)</label>
                        <input 
                            type="number" 
                            step="0.01"
                            value={config.service_charge} 
                            onChange={e => setConfig({...config, service_charge: parseFloat(e.target.value) || 0})}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9em' }}>Bill Footer Note</label>
                    <input 
                        type="text" 
                        value={config.footer_note} 
                        onChange={e => setConfig({...config, footer_note: e.target.value})}
                        placeholder="e.g. Thank you! Visit again."
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                </div>

                {msg.text && (
                    <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: msg.type === 'success' ? '#dcfce7' : '#fee2e2', color: msg.type === 'success' ? '#166534' : '#991b1b', fontSize: '0.9em', fontWeight: '600' }}>
                        {msg.text}
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={saving}
                    style={{ 
                        padding: '12px', 
                        borderRadius: '50px', 
                        border: 'none', 
                        backgroundColor: 'var(--primary-orange)', 
                        color: '#fff', 
                        fontWeight: '700', 
                        cursor: 'pointer',
                        opacity: saving ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(234, 88, 12, 0.2)'
                    }}
                >
                    {saving ? 'Saving...' : 'Save Configuration'}
                </button>
            </form>
        </div>
    );
};

export default BillingSettings;
